/**
 * ESCHER FALLS — 設定の保存。いまは音量2本だけ。
 *
 * `records.ts` と同じ流儀で、**決して例外を投げない**。
 * 読めない・壊れている・localStorage が使えない（プライベートモード等）でも
 * 既定値で普通に遊べること。設定が読めないだけで起動しないのが最悪である。
 */

const KEY = 'escher-falls/settings/v1';

export interface AudioSettings {
  /** BGM の音量 0..1。 */
  readonly bgm: number;
  /** 効果音の音量 0..1。 */
  readonly se: number;
}

/**
 * 既定値。
 *
 * BGM を控えめにしてあるのは、この遊びが**盤面をじっと見て考える**ものだから。
 * 音楽が前に出ると思考の邪魔になる。
 *
 * 効果音は操作の手応えなので前に出す。0.7 から 0.9 へ上げたのは、
 * 実機で「小さい」と言われたため。つまみは二乗してゲインに写しているので
 * （`engine.ts` の `toGain`）、0.7→0.9 は実際の音圧で約1.65倍になる。
 */
export const DEFAULT_AUDIO: AudioSettings = { bgm: 0.35, se: 0.9 };

function clamp01(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;
}

export function loadAudioSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_AUDIO;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_AUDIO;
    const o = parsed as Record<string, unknown>;
    return {
      bgm: clamp01(o.bgm, DEFAULT_AUDIO.bgm),
      se: clamp01(o.se, DEFAULT_AUDIO.se),
    };
  } catch {
    return DEFAULT_AUDIO;
  }
}

/** 保存する。失敗しても黙って諦める（音量が残らないだけ）。 */
export function saveAudioSettings(next: AudioSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ bgm: next.bgm, se: next.se }));
  } catch {
    // 保存できない環境でも遊びは続く。
  }
}
